import { forwardRef, lazy, Suspense, useState, type CSSProperties } from "react";
import type { Framing } from "../char3d/frame";
import type { FigureHandle } from "../char3d/view";
import { specFromEquipped, type CharSpec } from "../char3d/types";
import { Avatar, type Equipped } from "./Avatar";
import type { EmoteKind } from "../../shared/avatar";

/** three.js is a lazy chunk: the page shows the flat avatar until the 3D viewer has arrived (or for good, if WebGL is not available) */
const View = lazy(() => import("../char3d/view"));

export type { FigureHandle };

export interface FigureProps {
  equipped?: Equipped | null;
  /** or hand over a ready-made spec (the shop's try-on room does) */
  spec?: CharSpec;
  framing?: Framing;
  interactive?: boolean;
  sway?: boolean;
  floor?: boolean;
  className?: string;
  style?: CSSProperties;
  label?: string;
  onEmote?: (k: EmoteKind) => void;
  onReady?: () => void;
}

/** The big interactive 3D figure (drag to turn, click to make it react). Fills its parent. */
export const Figure = forwardRef<FigureHandle, FigureProps>(function Figure({ equipped, spec, label, className, style, ...rest }, ref) {
  const [failed, setFailed] = useState(false);
  const s = spec ?? specFromEquipped(equipped);
  const flat = (
    <div className={className} style={{ display: "grid", placeItems: "center", width: "100%", height: "100%", ...style }}>
      <Avatar equipped={equipped} size={140} label={label ?? "Din figur"} />
    </div>
  );
  if (failed) return flat;
  return (
    <Suspense fallback={flat}>
      <View ref={ref} spec={s} className={className} style={style} ariaLabel={label} onFail={() => setFailed(true)} {...rest} />
    </Suspense>
  );
});
